import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, Loading, Modal, inputCls, labelCls, btnPrimary, RoleBadge, fmtDateTime } from "@/components/kit";
import { ChatCircleDots, PaperPlaneRight, Plus, Megaphone, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [tab, setTab] = useState("messages");
  const [threads, setThreads] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null); // {user_id, name, role}
  const [convo, setConvo] = useState([]);
  const [text, setText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [anns, setAnns] = useState(null);
  const [teams, setTeams] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ audience: "school", title: "", body: "" });
  const endRef = useRef();
  const canAnnounce = ["athletic_director", "coach", "teacher"].includes(user?.role);

  const loadThreads = async () => { const r = await api.get("/messages/threads"); setThreads(r.data); };
  const loadAnns = async () => {
    const [a, t] = await Promise.all([api.get("/announcements"), canAnnounce ? api.get("/teams") : Promise.resolve({ data: [] })]);
    setAnns(a.data); setTeams(t.data);
  };
  useEffect(() => { loadThreads(); api.get("/messages/contacts").then((r) => setContacts(r.data)); }, []);
  useEffect(() => { if (tab === "announcements" && anns === null) loadAnns(); }, [tab]);

  const openConvo = async (t) => {
    setActive(t);
    const r = await api.get(`/messages/${t.user_id}`);
    setConvo(r.data);
    loadThreads();
    setTimeout(() => endRef.current?.scrollIntoView(), 100);
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const r = await api.post("/messages", { to_id: active.user_id, body: text.trim() });
    setConvo([...convo, r.data]);
    setText("");
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    loadThreads();
  };

  const startWith = (c) => { setNewOpen(false); openConvo({ user_id: c.id, name: c.name, role: c.role }); };

  const postAnn = async () => {
    if (!annForm.title || !annForm.body) { toast.error("Add a title and message"); return; }
    try { await api.post("/announcements", annForm); toast.success("Announcement posted"); setAnnOpen(false); setAnnForm({ audience: "school", title: "", body: "" }); loadAnns(); }
    catch (e) { toast.error("Could not post"); }
  };

  if (threads === null) return <Loading />;

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-6xl mx-auto">
      <PageHeader eyebrow="Communication" title="Hub" icon={ChatCircleDots} />

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("messages")} data-testid="tab-messages" className={`px-5 py-2.5 rounded-sm text-xs uppercase tracking-widest font-semibold ${tab === "messages" ? "bg-[#FF3B30] text-white" : "bg-[#141414] border border-white/10 text-[#A3A3A3]"}`}>Messages</button>
        <button onClick={() => setTab("announcements")} data-testid="tab-announcements" className={`px-5 py-2.5 rounded-sm text-xs uppercase tracking-widest font-semibold ${tab === "announcements" ? "bg-[#FF3B30] text-white" : "bg-[#141414] border border-white/10 text-[#A3A3A3]"}`}>Announcements</button>
      </div>

      {tab === "messages" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
          {/* Threads */}
          <Card className={`p-3 md:col-span-1 overflow-y-auto ${active ? "hidden md:block" : ""}`}>
            <button onClick={() => setNewOpen(true)} className={`${btnPrimary} w-full mb-3`} data-testid="new-message-button"><Plus size={16} weight="bold" /> New Message</button>
            {threads.length === 0 && <div className="text-sm text-[#737373] p-4 text-center">No conversations yet.</div>}
            {threads.map((t) => (
              <button key={t.user_id} onClick={() => openConvo(t)} data-testid={`thread-${t.user_id}`}
                className={`w-full flex items-center gap-3 px-2 py-3 rounded-sm text-left transition-colors ${active?.user_id === t.user_id ? "bg-[#FF3B30]/15" : "hover:bg-white/5"}`}>
                <Avatar name={t.name} size={38} />
                <div className="flex-1 min-w-0"><div className="text-sm text-white font-medium truncate">{t.name}</div><div className="text-xs text-[#737373] truncate">{t.last_body}</div></div>
                {t.unread > 0 && <span className="bg-[#FF3B30] text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5">{t.unread}</span>}
              </button>
            ))}
          </Card>

          {/* Conversation */}
          <Card className={`md:col-span-2 flex flex-col ${active ? "" : "hidden md:flex"}`}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-[#737373] text-sm">Select a conversation</div>
            ) : (
              <>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                  <button onClick={() => setActive(null)} className="md:hidden text-[#737373]"><ArrowLeft size={20} /></button>
                  <Avatar name={active.name} size={36} />
                  <div><div className="text-sm font-semibold text-white">{active.name}</div><RoleBadge role={active.role} /></div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="conversation">
                  {convo.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-sm text-sm ${m.mine ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] border border-white/10 text-white"}`}>
                        {m.body}
                        <div className={`text-[10px] mt-1 ${m.mine ? "text-white/60" : "text-[#737373]"}`}>{fmtDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="p-3 border-t border-white/10 flex gap-2">
                  <input className={inputCls} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" data-testid="message-input" />
                  <button onClick={send} className={btnPrimary} data-testid="send-message-button"><PaperPlaneRight size={18} weight="fill" /></button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {tab === "announcements" && (
        <div>
          {canAnnounce && <button onClick={() => setAnnOpen(true)} className={`${btnPrimary} mb-5`} data-testid="new-announcement-button"><Megaphone size={18} weight="fill" /> Post Announcement</button>}
          {anns === null ? <Loading /> : anns.length === 0 ? (
            <Card className="p-10 text-center text-[#737373]">No announcements yet.</Card>
          ) : (
            <div className="space-y-4">
              {anns.map((a) => (
                <Card key={a.id} className="p-5" data-testid={`ann-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="font-head text-xl font-bold uppercase tracking-tight">{a.title}</h3>
                    <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold shrink-0">{a.audience_name}</span>
                  </div>
                  <p className="text-[#A3A3A3] leading-relaxed mb-2">{a.body}</p>
                  <div className="text-[11px] text-[#737373]">{a.author_name} · {fmtDateTime(a.created_at)}</div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New message modal */}
      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="New Message" testid="contacts-modal">
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {contacts.map((c) => (
            <button key={c.id} onClick={() => startWith(c)} data-testid={`contact-${c.id}`} className="w-full flex items-center gap-3 px-2 py-3 rounded-sm hover:bg-white/5 text-left">
              <Avatar name={c.name} size={38} />
              <div className="flex-1"><div className="text-sm text-white font-medium">{c.name}</div></div>
              <RoleBadge role={c.role} />
            </button>
          ))}
          {contacts.length === 0 && <div className="text-sm text-[#737373] p-4">No contacts available.</div>}
        </div>
      </Modal>

      {/* New announcement modal */}
      <Modal open={annOpen} onClose={() => setAnnOpen(false)} title="Post Announcement" testid="announcement-modal">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Audience</label>
            <select className={inputCls} value={annForm.audience} onChange={(e) => setAnnForm({ ...annForm, audience: e.target.value })} data-testid="ann-audience-select">
              <option value="school">School-wide</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Title</label><input className={inputCls} value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="ann-title-input" /></div>
          <div><label className={labelCls}>Message</label><textarea rows={4} className={inputCls} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} data-testid="ann-body-input" /></div>
          <button onClick={postAnn} className={`${btnPrimary} w-full`} data-testid="post-announcement-button">Post</button>
        </div>
      </Modal>
    </div>
  );
}
